import React, { useState } from 'react';
import SectionEyebrow from '../components/SectionEyebrow';
import { usePage, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import Seo from '../components/Seo';
import { mediaApi } from '../lib/api';
import { toast } from 'sonner';
import { UploadCloud, FileText, Check } from 'lucide-react';

const Careers = () => {
  const c = usePage('careers');
  const perks = Array.isArray(c.perks) ? c.perks : [];
  const departments = Array.isArray(c.departments) ? c.departments : [];

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', position: '', availability: 'Full-time', startDate: '' });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.position) {
      toast.error('Please complete all required fields.');
      return;
    }
    if (!cv) {
      toast.error('Please attach your CV (PDF, DOC, or DOCX).');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('cv', cv);
      await mediaApi.post('/api/applications', fd);
      toast.success('Application sent! Our team will be in touch soon.');
      setForm({ firstName: '', lastName: '', email: '', phone: '', position: '', availability: 'Full-time', startDate: '' });
      setCv(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit your application.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f7f1e3]" data-testid="careers-page">
      <Seo title="Careers · Join the Team at Millpond Gatherings, Northford CT" description="Now hiring across kitchen, service, and bar at Millpond Gatherings in Northford, CT. Apply online with your CV." path="/careers" breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Careers', path: '/careers' }]} />
      {/* HERO */}
      <section className="relative h-[64vh] min-h-[460px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Careers at Millpond" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 30%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-28">
          <SectionEyebrow light>{c.heroEyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[48px] md:text-[72px] lg:text-[96px] tracking-[-0.02em] mt-4 leading-[1.02] max-w-4xl">
            {c.heroTitle}
          </h1>
          <p className="text-[#f1ead9]/90 mt-6 max-w-2xl text-base md:text-lg leading-relaxed">
            {c.heroBody}
          </p>
        </div>
      </section>

      {/* INTRO + PERKS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-[#942222] text-[40px] md:text-[52px] leading-[1.05] tracking-[-0.02em]">
              {c.introTitle}
            </h2>
            <p className="mt-6 text-[#942222]/80 text-[16px] leading-relaxed max-w-md">{c.introBody}</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {perks.map((p, i) => (
              <div key={i} className="border-t border-[#942222]/25 pt-6">
                <h3 className="font-serif text-[#942222] text-xl lg:text-2xl tracking-tight">{p.title}</h3>
                <p className="mt-3 text-[#942222]/75 text-[14px] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPEN ROLES */}
      <section className="py-16 lg:py-20 bg-[#efe6d0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionEyebrow>Open Roles</SectionEyebrow>
          <h2 className="font-serif text-[#942222] text-[36px] md:text-[52px] leading-[1.05] tracking-[-0.02em] mt-4">
            {"Where you'll fit in."}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {departments.map((d) => (
              <div key={d.name} className="border border-[#942222]/15 bg-white/50 p-7 lg:p-8">
                <p className="text-[11px] tracking-[0.28em] uppercase text-[#b8923e]">{d.name}</p>
                <ul className="mt-5 space-y-3">
                  {(d.roles || []).map((r) => (
                    <li key={r}>
                      <button
                        type="button"
                        onClick={() => { setForm((f) => ({ ...f, position: r })); document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }); }}
                        data-testid={`careers-role-${r.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        className="text-left w-full font-serif text-[#942222] text-lg tracking-tight hover:text-[#b13030] transition-colors flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e2b95a] group-hover:scale-150 transition-transform" />
                        {r}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="py-20 lg:py-28">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <div className="bg-[#942222] text-[#f1ead9] p-8 lg:p-12">
            <SectionEyebrow light>Application</SectionEyebrow>
            <h2 className="font-serif text-3xl lg:text-5xl tracking-tight mt-3">{c.formTitle}</h2>
            {c.formNote && <p className="mt-3 text-[#f1ead9]/70 text-sm max-w-lg">{c.formNote}</p>}

            <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              <Field label="First Name *" name="firstName" value={form.firstName} onChange={onChange} testid="careers-first-name" />
              <Field label="Last Name *" name="lastName" value={form.lastName} onChange={onChange} testid="careers-last-name" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="careers-email" />
              <Field label="Phone *" name="phone" type="tel" value={form.phone} onChange={onChange} testid="careers-phone" />

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Position *</label>
                <select name="position" value={form.position} onChange={onChange} data-testid="careers-position" className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors">
                  <option value="" className="bg-[#942222]">Select a position…</option>
                  {departments.map((d) => (
                    <optgroup key={d.name} label={d.name} className="bg-[#942222]">
                      {(d.roles || []).map((r) => (
                        <option key={r} value={r} className="bg-[#942222]">{r}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Availability</label>
                <select name="availability" value={form.availability} onChange={onChange} data-testid="careers-availability" className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors">
                  {['Full-time', 'Part-time', 'Seasonal'].map((o) => (
                    <option key={o} value={o} className="bg-[#942222]">{o}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Available to start</label>
                <input
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={onChange}
                  data-testid="careers-start-date"
                  className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors [color-scheme:dark]"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">CV / Resume *</label>
                <label data-testid="careers-cv-label" className="mt-2 flex items-center gap-3 cursor-pointer border-b border-[#f1ead9]/40 py-3 text-[#f1ead9]/90 hover:border-[#e2b95a] transition-colors">
                  {cv ? <FileText size={16} className="text-[#e2b95a]" /> : <UploadCloud size={16} className="text-[#e2b95a]" />}
                  <span className="text-sm truncate">{cv ? cv.name : 'Attach PDF, DOC, or DOCX'}</span>
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    data-testid="careers-cv-input"
                    onChange={(e) => setCv(e.target.files?.[0] || null)}
                  />
                </label>
              </div>

              <div className="md:col-span-2 mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="careers-submit"
                  className="inline-flex items-center gap-2 px-10 py-4 bg-[#e2b95a] hover:bg-[#cea444] text-[#942222] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors disabled:opacity-60"
                >
                  <Check size={14} /> {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = 'text', value, onChange, testid }) => (
  <div>
    <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors"
    />
  </div>
);

export default Careers;
